import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Avatar } from '../../../../../components';
import { EngagementParticipant } from '../../../../../store/deals/deals.types';
import { RootState } from '../../../../../store/store';

type Props = {
  participants: EngagementParticipant[];
};

export const ParticipantsRow: React.FC<Props> = ({
  participants,
}): JSX.Element => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleShowTooltip = () => {
    setShowTooltip(!showTooltip);
  };

  const namesRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    const isNameTarget = namesRef.current?.contains(target);
    if (!isNameTarget && showTooltip) setShowTooltip(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () =>
      document.removeEventListener('click', handleClickOutside, true);
  });

  const attrs = {
    wrapper: {
      className: 'partics-row',
    },
    names: {
      className: 'partics-row-names',
      onMouseEnter: handleShowTooltip,
      onMouseLeave: handleShowTooltip,
      ref: namesRef,
    },
    tooltip: {
      wrapper: {
        className: showTooltip
          ? 'open partics-row__tooltip'
          : 'partics-row__tooltip',
      },
      participant: {
        className: 'partics-row__tooltip__participant',
      },
      avatar: {
        className: 'partics-row__tooltip__participant-avatar',
        isTwentyPx: true,
      },
      name: {
        className: 'partics-row__tooltip__participant-name',
      },
    },
  };

  const allParticipants = participants
    ?.map((el) =>
      el.email === user?.email
        ? 'Me'
        : el?.metadata?.name?.split(' ')[0] || el?.metadata?.name
    )
    .join(', ');

  const createParticipantsSection = () => {
    const participantsString = allParticipants;

    const getCoordinatesForTooltip = () => {
      let xCoord = '';
      let yCoord = '';

      if (namesRef?.current?.getClientRects()?.length) {
        const { x, y } = namesRef.current.getClientRects()[0];
        xCoord = `${x}px`;
        yCoord = `${y + 27}px`;
      }
      return { xCoord, yCoord };
    };

    const { xCoord, yCoord } = getCoordinatesForTooltip();

    let tooltip: JSX.Element | null = null;
    if (participants?.length && allParticipants?.length) {
      const tooltipData = (
        <div
          {...attrs.tooltip.wrapper}
          style={
            yCoord && xCoord
              ? { position: 'fixed', top: yCoord, left: xCoord }
              : {}
          }
        >
          {participants.map((el, i) => (
            <div {...attrs.tooltip.participant} key={`${el.email}${i}`}>
              <Avatar
                image={el?.metadata?.avatarSrc ? el?.metadata?.avatarSrc : ''}
                userName={el?.metadata?.name}
                {...attrs.tooltip.avatar}
              />
              <span {...attrs.tooltip.name}>{el?.metadata?.name}</span>
            </div>
          ))}
        </div>
      );
      tooltip = tooltipData;
    }
    return { participantsString, tooltip };
  };

  const { participantsString, tooltip } = createParticipantsSection();

  return (
    <div {...attrs.wrapper}>
      {tooltip}
      <span {...attrs.names}>{participantsString}</span>
    </div>
  );
};
